import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit, Trash2, RefreshCw } from 'lucide-react';
import threeDotsIcon from '../../../../../assets/three-dots-icon.svg';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { financialEntryApi } from '../../api';
import { financialEntryRoutes } from '../../routes';
import { FinancialEntryListRow } from '../../types';
import { FINANCIAL_ENTRY_STATUS_OPTIONS } from '../../constants';
import StatusBadge from '../StatusBadge';
import {
  DropdownButton,
  DropdownContainer,
  DropdownIcon,
  DropdownItem,
  DropdownMenu,
  DropdownMenuPanel,
  SubMenu,
  SubMenuItem,
} from './style';

const MAIN_MENU_WIDTH = 200;
const SUB_MENU_WIDTH = 190;
const MENU_GAP = 4;

interface ActionsDropdownProps {
  entry: FinancialEntryListRow;
  onUpdated: () => void;
  onView: (entryId: number) => void;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ entry, onUpdated, onView }) => {
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const entryId = entry.entryId ?? entry.id;
  const [isOpen, setIsOpen] = useState(false);
  const [showStatusSubmenu, setShowStatusSubmenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 200,
    direction: 'down' as 'up' | 'down',
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = (submenuOpen: boolean) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const totalWidth = submenuOpen
      ? MAIN_MENU_WIDTH + SUB_MENU_WIDTH + MENU_GAP
      : MAIN_MENU_WIDTH;
    const menuHeight = submenuOpen ? 240 : 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const direction =
      spaceBelow < menuHeight && rect.top > menuHeight ? 'up' : 'down';

    setDropdownPosition({
      top: direction === 'up' ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - totalWidth),
      width: totalWidth,
      direction,
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition(showStatusSubmenu);
  }, [isOpen, showStatusSubmenu]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
      setShowStatusSubmenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      updateDropdownPosition(false);
    }
    setIsOpen((o) => !o);
    if (isOpen) setShowStatusSubmenu(false);
  };

  const handleStatusChange = async (status: number) => {
    try {
      setIsLoading(true);
      await financialEntryApi.update(entryId, { status });
      setFeedback({
        message: 'Status atualizado com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      setIsOpen(false);
      setShowStatusSubmenu(false);
      onUpdated();
    } catch {
      setFeedback({
        message: 'Erro ao atualizar status.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      await financialEntryApi.remove(entryId);
      setFeedback({
        message: 'Lançamento excluído com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      setShowDeleteModal(false);
      onUpdated();
    } catch {
      setFeedback({
        message: 'Erro ao excluir lançamento.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DropdownContainer>
        <DropdownButton ref={buttonRef} type="button" onClick={handleToggle}>
          <DropdownIcon src={threeDotsIcon} alt="Ações" />
        </DropdownButton>
      </DropdownContainer>

      {isOpen &&
        ReactDOM.createPortal(
          <DropdownMenu
            ref={dropdownRef}
            top={dropdownPosition.top}
            left={dropdownPosition.left}
            width={dropdownPosition.width}
            direction={dropdownPosition.direction}
          >
            {showStatusSubmenu && (
              <SubMenu>
                {FINANCIAL_ENTRY_STATUS_OPTIONS.map((opt) => (
                  <SubMenuItem
                    key={opt.value}
                    onClick={() => !isLoading && handleStatusChange(opt.value)}
                  >
                    <StatusBadge status={opt.value} />
                  </SubMenuItem>
                ))}
              </SubMenu>
            )}

            <DropdownMenuPanel>
              <DropdownItem
                onClick={() => {
                  setIsOpen(false);
                  onView(entryId);
                }}
              >
                <Eye size={16} />
                Visualizar
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  setIsOpen(false);
                  navigate(financialEntryRoutes.edit(entryId));
                }}
              >
                <Edit size={16} />
                Editar
              </DropdownItem>
              <DropdownItem
                $active={showStatusSubmenu}
                onClick={() => setShowStatusSubmenu((s) => !s)}
              >
                <RefreshCw size={16} />
                Alterar status
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  setIsOpen(false);
                  setShowDeleteModal(true);
                }}
              >
                <Trash2 size={16} />
                Excluir
              </DropdownItem>
            </DropdownMenuPanel>
          </DropdownMenu>,
          document.body,
        )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir lançamento"
        message={`Tem certeza que deseja excluir o lançamento #${entryId}?`}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setShowDeleteModal(false),
            variant: 'outline',
          },
          {
            label: isLoading ? 'Excluindo...' : 'Excluir',
            onClick: handleDelete,
            variant: 'danger',
          },
        ]}
      />
    </>
  );
};

export default ActionsDropdown;
